/**
 * Data validation utilities for TimechainStats
 *
 * Validates API responses against expected schemas to catch:
 * - Missing fields
 * - Type mismatches
 * - Malformed data structures
 * - Empty arrays that should have data
 */

/**
 * Get nested value from object using dot notation path
 * @param {Object} obj - Source object
 * @param {string} path - Dot notation path (e.g., 'btc_historical.Data.Data')
 * @returns {*} Value at path or undefined
 */
const getNestedValue = (obj, path) => {
  if (!path) return obj;
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Schema definitions for all Sidewinder API endpoints
 * Each schema defines the expected structure and critical fields
 */
const schemas = {
  // Timechain historical data endpoints (ts04-ts16)
  // These are complex time series data - just validate they return data
  ts04: {
    path: 'ts04',
    validate: (data) => data && typeof data === 'object'
  },
  ts05: {
    path: 'ts05',
    validate: (data) => data && typeof data === 'object'
  },
  ts06: {
    path: 'ts06',
    validate: (data) => data && typeof data === 'object'
  },
  ts07: {
    path: 'ts07',
    validate: (data) => data && typeof data === 'object'
  },
  ts08: {
    path: 'ts08',
    validate: (data) => data && typeof data === 'object'
  },
  ts09: {
    path: 'ts09',
    validate: (data) => data && typeof data === 'object'
  },
  ts10: {
    path: 'ts10',
    validate: (data) => data && typeof data === 'object'
  },
  ts11: {
    path: 'ts11',
    validate: (data) => data && typeof data === 'object'
  },
  ts13: {
    path: 'ts13',
    validate: (data) => data && typeof data === 'object'
  },
  ts14: {
    path: 'ts14',
    validate: (data) => data && typeof data === 'object'
  },
  ts15: {
    path: 'ts15',
    validate: (data) => data && typeof data === 'object'
  },
  ts16: {
    path: 'ts16',
    validate: (data) => data && typeof data === 'object'
  },

  // Snapshot endpoints (s01-s091)
  // Structure: {"s01": {"latest_snapshot": {...}}}
  s01: {
    path: 's01',
    fields: { latest_snapshot: 'object' }
  },
  // Structure: {"s02": {"stats": {...}}}
  s02: {
    path: 's02',
    fields: { stats: 'object' }
  },
  // Structure: {"s03": {"halvening": {...}, "bitcoin_stats": {...}}}
  s03: {
    path: 's03',
    fields: { halvening: 'object', bitcoin_stats: 'object' }
  },
  s04: {
    path: 's04',
    validate: () => true // Disabled endpoint, accept any structure
  },
  s06: {
    path: 's06',
    validate: () => true // Complex nested structure, skip validation
  },
  // Structure: {"s07": {"price": {"RAW": {...}, "DISPLAY": {...}}}}
  s07: {
    path: 's07',
    fields: { price: 'object' }
  },
  s08: {
    path: 's08',
    validate: () => true // Skip validation for now
  },
  s09: {
    path: 's09',
    validate: () => true // Disabled endpoint
  },
  s11: {
    path: 's11',
    validate: () => true // Skip validation for now
  },
  s12: {
    path: 's12',
    validate: () => true // Skip validation for now
  },
  s15: {
    path: 's15',
    validate: () => true // Skip validation for now
  },
  s16: {
    path: 's16',
    validate: () => true // Skip validation for now
  },
  s091: {
    path: 's091.btc_historical',
    validate: () => true // Known issue: often returns empty Data.Data array, using Diamondback fallback
  }
};

/**
 * Validate endpoint response against its schema
 * @param {string} endpointName - Endpoint identifier (e.g., 's091', 'ts04')
 * @param {Object} data - Response data to validate
 * @returns {Object} Validation result: { valid: boolean, errors: string[], warnings: string[] }
 */
export const validateEndpoint = (endpointName, data) => {
  const schema = schemas[endpointName];

  // No schema defined - assume valid
  if (!schema) {
    return { valid: true, errors: [], warnings: [`No schema defined for ${endpointName}`] };
  }

  const errors = [];
  const warnings = [];

  // Get the value at the specified path
  const value = getNestedValue(data, schema.path);

  // Check if path exists
  if (value === undefined) {
    errors.push(`Missing data at path: ${schema.path}`);
    return { valid: false, errors, warnings };
  }

  // Run custom validation function if provided
  if (schema.validate && typeof schema.validate === 'function') {
    try {
      const customValid = schema.validate(value);
      if (!customValid) {
        errors.push(`Custom validation failed for ${endpointName}`);
      }
    } catch (error) {
      errors.push(`Validation function error: ${error.message}`);
    }
  }

  // Validate field types
  if (schema.fields) {
    Object.entries(schema.fields).forEach(([field, expectedType]) => {
      const fieldValue = value?.[field];
      const actualType = typeof fieldValue;

      if (fieldValue === undefined || fieldValue === null) {
        warnings.push(`Field '${field}' is ${fieldValue === null ? 'null' : 'undefined'}`);
      } else if (actualType !== expectedType) {
        errors.push(
          `Field '${field}' type mismatch: expected ${expectedType}, got ${actualType}`
        );
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validate multiple endpoints at once
 * @param {Object} dataByEndpoint - Object with endpoint names as keys
 * @returns {Object} Validation results by endpoint
 */
export const validateAllEndpoints = (dataByEndpoint) => {
  const results = {};

  Object.entries(dataByEndpoint).forEach(([endpoint, data]) => {
    results[endpoint] = validateEndpoint(endpoint, { [endpoint]: data });
  });

  return results;
};

/**
 * Get summary of validation results
 * @param {Object} validationResults - Results from validateAllEndpoints
 * @returns {Object} Summary statistics
 */
export const getValidationSummary = (validationResults) => {
  let valid = 0;
  let invalid = 0;
  let warnings = 0;

  Object.values(validationResults).forEach(result => {
    if (result.valid) {
      valid++;
    } else {
      invalid++;
    }
    warnings += result.warnings?.length || 0;
  });

  return {
    total: Object.keys(validationResults).length,
    valid,
    invalid,
    warnings,
    successRate: ((valid / (valid + invalid)) * 100).toFixed(1)
  };
};
